import { IconSymbol } from '@/components/ui/IconSymbol'
import { Image } from '@/components/ui/image'
import { Pressable } from '@/components/ui/pressable'
import { isBelowiOS26 } from '@/lib/utils/iosCompat'
import { useAppSelector } from '@/redux/store'
import { useRouter, type NativeStackHeaderItem } from 'expo-router'
import { useTheme } from 'expo-router/react-navigation'
import { UserCircleIcon } from 'lucide-react-native'
import { Platform, View } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withSpring, withTiming } from 'react-native-reanimated'

// Matches the 44pt glass bar buttons beside it on iOS 26; older and non-iOS bars are shorter.
const isGlassBar = Platform.OS === 'ios' && !isBelowiOS26
const AVATAR_SIZE = isGlassBar ? 44 : 36
const AVATAR_SIZE_CLASS = isGlassBar ? 'w-[44px] h-[44px]' : 'w-[36px] h-[36px]'

// A custom header item gets no system press feedback, so mirror what UIKit gives the native bar buttons
// beside it: the glass bounce on iOS 26, a dimmed highlight before that.
const PRESSED_SCALE = 1.15
const PRESS_SPRING = { damping: 12, stiffness: 260, mass: 0.6 }

// 「我的」 left the tab bar to make room for 收款, so the tab roots (except search) open it from an avatar in the header.
export default function useProfileHeaderItems(): NativeStackHeaderItem[] {
  return [
    {
      type: 'custom',
      hidesSharedBackground: true,
      element: <HeaderAvatar />,
    },
  ]
}

function HeaderAvatar() {
  const router = useRouter()
  const theme = useTheme()
  const avatarUrl = useAppSelector(state => state.currentUser.user?.avatarUrl)
  const displayName = useAppSelector(state => state.currentUser.user?.displayName)

  const scale = useSharedValue(1)
  const opacity = useSharedValue(1)
  const animatedStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.get() }], opacity: opacity.get() }))

  const handlePressIn = () => {
    if (Platform.OS !== 'ios') return
    if (isGlassBar) scale.set(withSpring(PRESSED_SCALE, PRESS_SPRING))
    else opacity.set(withTiming(0.4, { duration: 80 }))
  }

  const handlePressOut = () => {
    if (Platform.OS !== 'ios') return
    if (isGlassBar) scale.set(withSpring(1, PRESS_SPRING))
    else opacity.set(withTiming(1, { duration: 200 }))
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="我的"
      onPress={() => router.push('/profile')}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      // Off iOS the title sits right beside the header-left items, so leave a gap before it
      className={`${AVATAR_SIZE_CLASS} ${Platform.OS === 'ios' ? '' : 'mr-2'}`}
    >
      {/* Scale outside the circular clip so the enlarged avatar isn't cut by its own mask */}
      <Animated.View style={animatedStyle}>
        <View
          style={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            borderRadius: AVATAR_SIZE / 2,
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {avatarUrl ? (
            <Image source={{ uri: avatarUrl }} alt={displayName ?? '我的'} size="none" className={AVATAR_SIZE_CLASS} />
          ) : Platform.OS === 'ios' ? (
            <IconSymbol name="person.circle" size={AVATAR_SIZE} color={theme.colors.primary} />
          ) : (
            // Same placeholder icon the profile page's TableView row uses off iOS
            <UserCircleIcon size={AVATAR_SIZE} color={theme.colors.primary} />
          )}
        </View>
      </Animated.View>
    </Pressable>
  )
}
